import React from 'react';
import PropTypes from 'prop-types';
import Button from '@material-ui/core/Button';
import BaseDialog from 'core/components/base-dialog';

import { Label } from 'core/components/label';
import useStyles from './warning-modal.styles';

const reasonsData = {
  // Shown when an agent previews a "Create New Change" on a date that already has a change on it
  // (#31068). Recoverable by design — closing returns to the form with the draft intact, so the
  // agent can pick a different date or switch to "Edit Selected Change".
  CHANGE_DATE_CONFLICT: {
    title: 'Change date conflict',
    description:
      'A change already exists for this effective date. To continue, choose a different date or switch to "Edit Selected Change" to update the existing change.'
  },
  // Shown when the active term is endorsed while an at-renewal "Renewal Change" segment already
  // exists (#34799). The renewal is generated from the Renewal Change segment, which does NOT pick
  // up mid-term changes — so without also updating it, this change is lost at renewal. Non-blocking:
  // closing returns to the preview so the agent can still continue.
  // Editing the Renewal Change segment is underwriting-permissioned, which agency (IA) users don't
  // have, so `agencyDescription` sends them to Agency Support instead of asking them to do it.
  PENDING_RENEWAL_CHANGE_NOT_CAPTURED: {
    title: 'This policy has a pending Renewal Change',
    description:
      'A "Renewal Change" has already been set up on this policy, and the upcoming renewal will be generated from it. Changes you make to the current term are NOT automatically copied into that Renewal Change, so this update will be lost when the policy renews unless the Renewal Change is updated too. Please also apply this change to the Renewal Change segment (an underwriting-permissioned change) so it carries into the renewal.',
    agencyDescription:
      'A "Renewal Change" is already set up on this policy and the upcoming renewal will be generated from it. This update will NOT carry into the renewal on its own. Please contact Agency Support to have this change applied to the Renewal Change as well.'
  }
};

function WarningModal({ onClose, open, reason, isAgency = false }) {
  const classes = useStyles();
  const { title, description: internalDescription, agencyDescription } = reasonsData[reason];
  const description = isAgency && agencyDescription ? agencyDescription : internalDescription;
  return (
    <BaseDialog size="lg" open={open}>
      <div className={classes.container}>
        <Label className={classes.title} type="greenBig">
          {title}
        </Label>
        <Label type="body1">{description}</Label>
        <div className={classes.footer}>
          <Button className={classes.button} variant="contained" color="primary" type="button" onClick={onClose}>
            Close
          </Button>
        </div>
      </div>
    </BaseDialog>
  );
}

WarningModal.propTypes = {
  onClose: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
  reason: PropTypes.string.isRequired,
  isAgency: PropTypes.bool
};

export default WarningModal;
